import axios from 'axios';

// Base URL for the backend API
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// Log the API URL for debugging
console.log('API Base URL:', API_BASE_URL);

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
  // Timeout after 30 seconds (helpful for slow mobile connections)
  timeout: 30000,
});

// Add token to requests if it exists
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle token expiration/invalid token
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      // Token is invalid or expired
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      // Redirect to login page
      if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// API functions

// Health check
export const checkHealth = async () => {
  const response = await api.get('/api/health');
  return response.data;
};

// Database test
export const testDatabase = async () => {
  const response = await api.get('/api/db-test');
  return response.data;
};

// Waitlist with retry logic for mobile connections
export const joinWaitlist = async (payload, retries = 2) => {
  try {
    const response = await api.post('/api/waitlist', payload);
    return response.data;
  } catch (error) {
    // Retry on network errors (not on validation errors)
    if (retries > 0 && !error.response) {
      // Wait before retrying (exponential backoff: 1s, then 2s)
      const delay = (3 - retries) * 1000;
      await new Promise(resolve => setTimeout(resolve, delay));
      return joinWaitlist(payload, retries - 1);
    }
    throw error;
  }
};

// Auth endpoints (we'll implement these in the backend later)
export const register = async (userData) => {
  const response = await api.post('/api/auth/register', userData);
  return response.data;
};

export const login = async (credentials) => {
  const response = await api.post('/api/auth/login', credentials);
  return response.data;
};

// Two-factor auth helpers
export const setupTwoFactor = async () => {
  const response = await api.post('/api/auth/2fa/setup');
  return response.data;
};

export const verifyTwoFactor = async (token) => {
  const response = await api.post('/api/auth/2fa/verify', { token });
  return response.data;
};

export const disableTwoFactor = async () => {
  const response = await api.post('/api/auth/2fa/disable');
  return response.data;
};

export const logout = () => {
  localStorage.removeItem('token');
};

// User endpoints
export const getCurrentUser = async () => {
  const response = await api.get('/api/auth/me');
  return response.data;
};

// Task endpoints
export const getTasks = async () => {
  const response = await api.get('/api/tasks');
  return response.data;
};

export const createTask = async (taskData) => {
  const response = await api.post('/api/tasks', taskData);
  return response.data;
};

export const completeTask = async (taskId) => {
  const response = await api.post(`/api/tasks/${taskId}/complete`);
  return response.data;
};

// Project endpoints
export const getProjects = async (includeArchived = false) => {
  const response = await api.get(`/api/projects?include_archived=${includeArchived}`);
  return response.data;
};

export const getProject = async (projectId) => {
  const response = await api.get(`/api/projects/${projectId}`);
  return response.data;
};

export const createProject = async (projectData) => {
  const response = await api.post('/api/projects', projectData);
  return response.data;
};

export const updateProject = async (projectId, projectData) => {
  const response = await api.put(`/api/projects/${projectId}`, projectData);
  return response.data;
};

export const deleteProject = async (projectId) => {
  const response = await api.delete(`/api/projects/${projectId}`);
  return response.data;
};

export const getProjectStats = async (projectId) => {
  const response = await api.get(`/api/projects/${projectId}/stats`);
  return response.data;
};

// Admin Waitlist Management
export const getWaitlistSignups = async () => {
  const response = await api.get('/api/admin/waitlist');
  return response.data;
};

export const sendInvites = async (signupIds) => {
  const response = await api.post('/api/admin/waitlist/send-invites', { signupIds });
  return response.data;
};

export const resendInvite = async (id) => {
  const response = await api.post(`/api/admin/waitlist/resend-invite/${id}`);
  return response.data;
};

// More endpoints will be added as we build features

export default api;
